import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Custom hook for scroll hijacking behavior
 * @param {Object} options - Configuration options
 * @param {React.RefObject} options.sectionRef - Reference to the section element
 * @param {number} options.totalSteps - Total number of steps/items to navigate through
 * @param {Function} options.onStepChange - Callback when step changes (receives index and direction)
 * @param {Function} options.onEnterSection - Callback when entering the section (receives direction)
 * @param {Function} options.onExitSection - Callback when exiting the section (receives direction)
 * @returns {Object} - State and controls for scroll hijacking
 */
const useScrollHijack = ({
  sectionRef,
  totalSteps,
  onStepChange,
  onEnterSection,
  onExitSection
}) => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [isInSection, setIsInSection] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(null);
  
  const scrollTriggerRef = useRef(null);
  const wheelEventsRef = useRef([]);
  const lastScrollY = useRef(0);
  const touchStartY = useRef(0);
  
  // Initialize ScrollTrigger
  useEffect(() => {
    if (!sectionRef.current) return;
    
    // Set up ScrollTrigger
    const trigger = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: "top 70%", // Trigger earlier when coming from below
      end: "bottom 30%", // End later to keep section active longer
      onEnter: () => handleSectionEnter('down'),
      onLeaveBack: () => handleSectionExit('up'),
      onLeave: () => handleSectionExit('down'),
      onEnterBack: () => {
        // Special handling for entering back (upward scroll)
        console.log('Entering back (upward)');
        handleSectionEnter('up');
      },
      markers: false,
      refreshPriority: -1,
      // Add pin to keep section in view
      pin: false, // We'll handle positioning manually
      anticipatePin: 1
    });
    
    scrollTriggerRef.current = trigger;
    
    return () => {
      if (trigger) trigger.kill();
    };
  }, [sectionRef]);
  
  // Handle entering the section
  const handleSectionEnter = (direction) => {
    console.log(`Entering section from ${direction}`);
    
    // Disable normal scrolling
    document.body.style.overflow = 'hidden';
    setIsInSection(true);
    
    // Center the section properly, especially for upward scroll
    setTimeout(() => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const sectionTop = rect.top + window.pageYOffset;
        const sectionHeight = rect.height;
        
        // Calculate position to center the section
        const targetScrollPosition = sectionTop - (viewportHeight - sectionHeight) / 2;
        
        // Smooth scroll to center the section
        window.scrollTo({
          top: targetScrollPosition,
          behavior: direction === 'up' ? 'smooth' : 'auto'
        });
      }
    }, direction === 'up' ? 100 : 0); // Small delay for upward scroll
    
    // Set initial position based on direction
    if (direction === 'down') {
      setCurrentStep(0); // First item when scrolling down into section
    } else {
      setCurrentStep(totalSteps - 1); // Last item when scrolling up into section
    }
    
    // Call the callback with slight delay to ensure positioning is done
    setTimeout(() => {
      if (onEnterSection) {
        onEnterSection(direction);
      }
    }, direction === 'up' ? 200 : 50);
  };
  
  // Handle exiting the section
  const handleSectionExit = (direction) => {
    console.log(`Exiting section toward ${direction}`);
    
    // Re-enable normal scrolling
    document.body.style.overflow = '';
    setIsInSection(false);
    setCurrentStep(-1);
    setIsAnimating(false); // Reset animation state
    
    // Call the callback
    if (onExitSection) {
      onExitSection(direction);
    }
  };
  
  // Handle scrolling within the section
  const handleSectionScroll = (direction) => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    let nextStep;
    if (direction === 'down') {
      nextStep = Math.min(currentStep + 1, totalSteps);
      
      // If we're at the last item + 1 (which is the exit animation step)
      if (nextStep === totalSteps) {
        // Call the callback with a special index to indicate exit animation
        if (onStepChange) {
          onStepChange(nextStep, direction, true); // true indicates exit animation
        }
        
        // After animation duration, allow scrolling to continue
        setTimeout(() => {
          document.body.style.overflow = '';
          setIsInSection(false);
          setCurrentStep(-1);
        }, 800); // Slightly longer to allow exit animation to complete
        
        // Set animating to false after a longer delay
        setTimeout(() => {
          setIsAnimating(false);
        }, 1000);
        
        return;
      }
    } else {
      nextStep = Math.max(currentStep - 1, -1);
      
      // If we're going back above the first item
      if (nextStep === -1) {
        // Call the callback with a special index to indicate exit animation
        if (onStepChange) {
          onStepChange(nextStep, direction, true); // true indicates exit animation
        }
        
        // After animation duration, allow scrolling to continue
        setTimeout(() => {
          document.body.style.overflow = '';
          setIsInSection(false);
          setCurrentStep(-1);
        }, 800);
        
        // Set animating to false after a longer delay
        setTimeout(() => {
          setIsAnimating(false);
        }, 1000);
        
        return;
      }
    }
    
    // Call the callback
    if (onStepChange) {
      onStepChange(nextStep, direction, false); // false indicates normal step change
    }
    
    // Update state
    setCurrentStep(nextStep);
    setTimeout(() => {
      setIsAnimating(false);
    }, 600); // Match this with your animation duration
  };
  
  // Set up wheel event listener
  useEffect(() => {
    const handleWheel = (e) => {
      if (!isInSection) return;
      
      // Store wheel events to determine direction more accurately
      const now = Date.now();
      wheelEventsRef.current.push({
        deltaY: e.deltaY,
        timestamp: now
      });
      
      // Only keep events from the last 200ms
      wheelEventsRef.current = wheelEventsRef.current.filter(event => now - event.timestamp < 200);
      
      // Calculate average direction
      const avgDelta = wheelEventsRef.current.reduce((sum, event) => sum + event.deltaY, 0) / wheelEventsRef.current.length;
      const direction = avgDelta > 0 ? 'down' : 'up';
      setScrollDirection(direction);
      
      if (isInSection && !isAnimating) {
        e.preventDefault();
        e.stopPropagation();
        
        // Handle scroll within the section
        handleSectionScroll(direction);
      }
    };
    
    // Use wheel event for more precise control
    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [isInSection, isAnimating, currentStep, totalSteps]);
  
  // Set up touch events for mobile
  useEffect(() => {
    const handleTouchStart = (e) => {
      if (!isInSection) return;
      touchStartY.current = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e) => {
      if (!isInSection || isAnimating) return;
      
      const touchY = e.touches[0].clientY;
      const diff = touchStartY.current - touchY;
      
      if (Math.abs(diff) > 50) { // Threshold to trigger animation
        const direction = diff > 0 ? 'down' : 'up';
        setScrollDirection(direction);
        handleSectionScroll(direction);
        touchStartY.current = touchY; // Reset for next movement
      }
    };
    
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isInSection, isAnimating, currentStep, totalSteps]);
  
  return {
    currentStep,
    isInSection,
    isAnimating,
    scrollDirection,
    setCurrentStep,
    setIsAnimating
  };
};

export default useScrollHijack;